import { z } from "zod";

import { looseArray } from "../looseParse";
import {
  BENEFICIARY_RELATIONSHIPS,
  BENEFICIARY_TYPES,
  DOCUMENT_SUB_TYPES,
  DOCUMENT_TYPES,
  INVESTMENT_OBJECTIVE_VALUES,
  INVESTMENT_TIME_HORIZON_VALUES,
  LIQUIDITY_NEEDS_VALUES,
  OPTIONS_LEVELS,
  REVIEW_STATUS,
  RISK_TOLERANCE_VALUES,
} from "./account.constants";

/**
 * Zod schemas mirroring the REST response shapes for `account.ts`. These are the
 * source of truth: the module re-exports `z.infer` of each schema as its public
 * type. `Date`-typed fields use `z.coerce.date()` so the inferred type stays
 * `Date` (matching existing consumers) while string payloads still parse.
 *
 * IMPORTANT: `z.coerce.date()` alone coerces `null` to epoch (`1970-01-01`) —
 * a silent mutation, not a parse failure. Any API field that can be null must
 * use `.nullish()` (or `.nullable()`) so null stays null.
 */

/** Coerce ISO/string dates; reject/preserve null via `.nullish()` at call sites. */
const coerceDate = z.coerce.date();
const coerceDateNullish = coerceDate.nullish();

export const tradeAccountSchema = z.object({
  id: z.string(),
  account_number: z.string(),
  status: z.string(),
  crypto_status: z.string(),
  currency: z.string(),
  buying_power: z.string(),
  regt_buying_power: z.string(),
  daytrading_buying_power: z.string(),
  effective_buying_power: z.string(),
  non_marginable_buying_power: z.string(),
  bod_dtbp: z.string(),
  cash: z.string(),
  cash_withdrawable: z.string(),
  cash_transferable: z.string(),
  accrued_fees: z.string(),
  pending_transfer_out: z.string(),
  pending_transfer_in: z.string(),
  portfolio_value: z.string(),
  pattern_day_trader: z.boolean(),
  trading_blocked: z.boolean(),
  transfers_blocked: z.boolean(),
  account_blocked: z.boolean(),
  created_at: coerceDate,
  trade_suspended_by_user: z.boolean(),
  multiplier: z.string(),
  shorting_enabled: z.boolean(),
  equity: z.string(),
  last_equity: z.string(),
  long_market_value: z.string(),
  short_market_value: z.string(),
  position_market_value: z.string(),
  initial_margin: z.string(),
  maintenance_margin: z.string(),
  last_maintenance_margin: z.string(),
  sma: z.string(),
  daytrade_count: z.number(),
  balance_asof: z.string(),
  clearing_broker: z.string(),
});

// Backends return this object with a variable subset of keys (and omit the rest
// entirely), so every field is optional — a missing key must not drop the
// parent account under looseArray.
export const userConfigurationsSchema = z.object({
  max_margin_multiplier: z.string().optional(),
  no_shorting: z.boolean().optional(),
  suspend_trade: z.boolean().optional(),
  fractional_trading: z.boolean().optional(),
  trade_confirm_email: z.string().optional(),
  trade_suspended_by_user: z.boolean().optional(),
  ptp_no_exception_entry: z.boolean().optional(),
  max_options_trading_level: z.number().optional(),
});

// `.catch(null)` keeps the useful `null | "30k" | "100k"` literal type for
// consumers while ensuring an unrecognized/new tier from the backend degrades
// to `null` (treated as non-elite) instead of failing the parse — which, under
// `looseArray(accountSchema)`, would otherwise drop the whole account.
export const eliteStatusSchema = z
  .union([z.null(), z.literal("30k"), z.literal("100k")])
  .catch(null);

export const accountCashInterestSchema = z.object({
  currency: z.string().optional(),
  status: z.string().optional(),
  apr_tier_name: z.string().nullable().optional(),
});

// Known values are "margin" | "cash". Widened to a plain string so an
// unexpected trading type from the backend can't drop the entire account when
// the list is parsed via `looseArray(accountSchema)`. The `TradingType` type
// is not consumed as a narrowed literal anywhere, so this is type-safe.
export const tradingTypeSchema = z.string();

export const beneficiarySchema = z.object({
  given_name: z.string(),
  middle_name: z.string().nullable(),
  family_name: z.string(),
  date_of_birth: z.string(),
  tax_id: z.string(),
  tax_id_type: z.string(),
  relationship: z.enum(BENEFICIARY_RELATIONSHIPS),
  type: z.enum(BENEFICIARY_TYPES),
  share_pct: z.number(),
});

// NOTE: field optionality/nullability below is validated against real Broker
// API `/accounts` payloads. Because this schema is parsed via
// `looseArray(accountSchema)`, ANY over-strict field drops the *entire* account
// from the app (empty account list -> blank dashboard). Only `id` is treated as
// guaranteed; everything the backend can omit or send as `null` is
// optional/nullable. `account_number` is NOT guaranteed: gobroker models it as a
// nullable column and only issues a number at KYC/submit, so a pre-onboarding
// PAPER_ONLY account has none.
export const accountSchema = z.object({
  account_number: z.string().nullish(),
  account_name: z.string().nullish(),
  account_type: z.string().nullish(),
  account_sub_type: z.string().nullish(),
  trading_type: tradingTypeSchema,
  // Optional: some onboarding/partial accounts omit this object entirely.
  // Consumers already optional-chain (`admin_configurations?.…`).
  admin_configurations: z
    .object({
      allow_instant_ach: z.boolean().nullish(),
      max_options_trading_level: z.number().nullish(),
      disable_crypto: z.boolean().nullish(),
    })
    .nullish(),
  balance_asof: z.string(),
  can_limited_fund: z.boolean().nullish(),
  // Unknown capability statuses must not drop the whole account under looseArray.
  capabilities: z
    .object({
      bank_linkable_status: z
        .enum(["disallowed", "allowed"])
        .nullish()
        .catch(undefined),
      depositable_status: z
        .enum(["disallowed", "limited", "allowed"])
        .nullish()
        .catch(undefined),
      withdrawable_status: z
        .enum(["disallowed", "limited", "allowed"])
        .nullish()
        .catch(undefined),
    })
    .nullish(),
  clearing_broker: z.string(),
  correspondent: z.string(),
  created_at: z.string(),
  crypto_broker: z.string().nullish(),
  crypto_status: z.string(),
  crypto_tier: z.number().nullish(),
  day_trade_count: z.number(),
  email: z.string(),
  elite_status: eliteStatusSchema,
  equity: z.string(),
  id: z.string(),
  kyc_provider: z.string().nullish(),
  last_onboarding_step: z.string().nullish(),
  maintenance_margin: z.string(),
  marked_risky_transfers_at: z.string().nullish(),
  multiplier: z.string(),
  name: z.string(),
  onfido_applicant_id: z.string().nullish(),
  onfido_details: z
    .object({
      applicant_id: z.string().nullish(),
    })
    .nullable()
    .nullish(),
  options_approved_level: z.number().nullish(),
  options_trading_level: z.number().nullish(),
  // Backend sends a boolean here (the string form only appears on the legacy
  // trade-account shape); accept both so neither variant drops the account.
  shorting_enabled: z.union([z.boolean(), z.string()]).nullish(),
  status: z.string(),
  user_configurations: userConfigurationsSchema.nullish(),
  currency: z.string(),
  // These onboarding enums carry legacy/empty values on older accounts.
  // `.catch(undefined)` keeps the narrow literal type for consumers while an
  // unrecognized value degrades to `undefined` rather than dropping the account.
  investment_time_horizon: z
    .enum(INVESTMENT_TIME_HORIZON_VALUES)
    .nullish()
    .catch(undefined),
  investment_objective: z
    .enum(INVESTMENT_OBJECTIVE_VALUES)
    .nullish()
    .catch(undefined),
  liquidity_needs: z.enum(LIQUIDITY_NEEDS_VALUES).nullish().catch(undefined),
  risk_tolerance: z.enum(RISK_TOLERANCE_VALUES).nullish().catch(undefined),
  // Per-item: one malformed beneficiary (e.g. an unexpected relationship/type
  // enum) must not drop the entire account when parsed via looseArray.
  beneficiaries: looseArray(
    beneficiarySchema,
    "accountSchema.beneficiaries",
  ).optional(),
});

export const positionSchema = z.object({
  asset_id: z.string(),
  symbol: z.string(),
  exchange: z.string(),
  asset_class: z.string(),
  asset_marginable: z.boolean(),
  qty: z.string(),
  avg_entry_price: z.string(),
  side: z.string(),
  market_value: z.string().nullable(),
  cost_basis: z.string(),
  unrealized_pl: z.string().nullable(),
  unrealized_plpc: z.string().nullable(),
  unrealized_intraday_pl: z.string().nullable(),
  unrealized_intraday_plpc: z.string().nullable(),
  current_price: z.string().nullable(),
  lastday_price: z.string().nullable(),
  change_today: z.string().nullable(),
  // Present on most equity positions; optional so a missing field can't blank
  // the whole positions list under looseArray.
  qty_available: z.string().nullish(),
});

export const ownerDetailsSchema = z.object({
  // Validated against real `/accounts/{id}/details` payloads: `permanent_resident`
  // arrives as null, and the income/net-worth values are decimal strings (e.g.
  // "1000000000"), not numbers. Coerce the money fields (consumers already read
  // them via `Number(...)` / feed them into `z.coerce.number()` forms) and
  // tolerate null so getDetails stops silently falling back to the raw payload.
  permanent_resident: z.boolean().nullish(),
  annual_income_max: z.coerce.number().nullish(),
  annual_income_min: z.coerce.number().nullish(),
  // Bare (non min/max) totals are omitted on the real payload.
  liquid_net_worth: z.coerce.number().nullish(),
  total_net_worth: z.coerce.number().nullish(),
  city: z.string().nullish(),
  unit: z.string().nullish(),
  country_of_citizenship: z.string().nullish(),
  country_of_tax_residence: z.string().nullish(),
  created_at: z.string().nullish(),
  crypto_agreement_signed_at: coerceDateNullish,
  crypto_agreement_revision: z.string().nullish(),
  customer_agreement_signed_at: coerceDateNullish,
  margin_agreement_signed_at: coerceDateNullish,
  options_agreement_signed_at: coerceDateNullish,
  opra_agreement_signed_at: coerceDateNullish,
  date_of_birth: z.string().nullish(),
  employer_address: z.string().nullish(),
  employer: z.string().nullish(),
  employment_status: z.string().nullish(),
  country_of_birth: z.string().nullish(),
  visa_type: z.string().nullish(),
  visa_type_other_free_text: z.string().nullish(),
  visa_expiration_date: coerceDateNullish,
  middle_name: z.string().nullish(),
  family_name: z.string().nullish(),
  function: z.string().nullish(),
  funding_source: z.array(z.string()).nullish(),
  given_name: z.string().nullish(),
  immediate_family_exposed: z.boolean().nullish(),
  is_affiliated_exchange_or_finra: z.boolean().nullish(),
  is_control_person: z.boolean().nullish(),
  is_discretionary: z.boolean().nullish(),
  is_politically_exposed: z.boolean().nullish(),
  is_professional: z.boolean().nullish(),
  // A 2021 funding-promotion flag (ENG-8536). Not present in the sampled
  // `/accounts/{id}/details` payloads used to validate this schema, and absent
  // from the public reference, but a promo flag would plausibly only be sent for
  // enrolled accounts — so it is modelled as nullish rather than dropped.
  funding_special_enabled: z.boolean().nullish(),
  legal_name: z.string().nullish(),
  liquid_net_worth_max: z.coerce.number().nullish(),
  liquid_net_worth_min: z.coerce.number().nullish(),
  utm_params: z
    .object({
      growsurf_referrer_id: z.string().nullish(),
    })
    .nullish(),
  owner_id: z.string().nullish(),
  phone_number: z.string().nullish(),
  position: z.string().nullish(),
  postal_code: z.string().nullish(),
  state: z.string().nullish(),
  street_address: z.array(z.string()).nullish(),
  tax_id_type: z.string().nullish(),
  total_net_worth_max: z.coerce.number().nullish(),
  total_net_worth_min: z.coerce.number().nullish(),
  investment_experience_with_options: z.string().nullish(),
  investment_experience_with_stocks: z.string().nullish(),
  esign_audit: z
    .array(
      z.object({
        agreement: z.string(),
        signed_at: z.string(),
        ip_address: z.string(),
        revision: z.string(),
      }),
    )
    .nullish(),
  has_spreads_knowledge: z.boolean().nullish(),
  marital_status: z.string().nullish(),
  number_of_dependents: z.number().nullish(),
  review_status: z.enum(REVIEW_STATUS).nullish(),
  exchange_questions: z
    .object({
      NYSE: z
        .object({
          qA: z.boolean().nullish(),
          qB: z.boolean().nullish(),
          qC: z.boolean().nullish(),
          qD: z.boolean().nullish(),
          qE: z.boolean().nullish(),
          qF: z.boolean().nullish(),
          qG: z.boolean().nullish(),
          qH: z.boolean().nullish(),
          qI: z.boolean().nullish(),
          qJ: z.boolean().nullish(),
          qK: z.boolean().nullish(),
          occupations: z.string().nullish(),
          employer_name_address: z.string().nullish(),
          address: z.string().nullish(),
          name: z.string().nullish(),
        })
        .nullish(),
    })
    .nullish(),
});

export const ownerSchema = z.object({
  owner_id: z.string(),
  is_ubo: z.boolean(),
  is_authorized_individual: z.boolean(),
  is_legal_entity: z.boolean(),
  is_legal_account_opener: z.boolean(),
  email: z.string().nullable(),
  given_name: z.string().nullable(),
  middle_name: z.string().nullable(),
  family_name: z.string().nullable(),
  date_of_birth: z.string().nullable(),
  phone_number: z.string().nullable(),
  country_of_tax_residence: z.string().nullable(),
  country_of_citizenship: z.string().nullable(),
  // Real `/owners` payloads send address/funding as arrays of strings (matching
  // ownerDetailsSchema); accept string too so neither variant drops the owner.
  street_address: z
    .union([z.string(), z.array(z.string())])
    .nullable()
    .nullish(),
  unit: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postal_code: z.string().nullable(),
  country: z.string().nullable(),
  tax_id_type: z.string().nullable(),
  title_at_company: z.string().nullable(),
  is_politically_exposed: z.boolean().nullable(),
  immediate_family_exposed: z.boolean().nullish(),
  is_control_person: z.boolean().nullable(),
  controlling_firms: z.union([z.string(), z.array(z.string())]).nullish(),
  is_affiliated_exchange_or_finra: z.boolean().nullable(),
  affiliated_firm: z.string().nullable(),
  legal_name: z.string().nullable(),
  country_of_incorporation: z.string().nullable(),
  state_of_incorporation: z.string().nullable(),
  date_of_incorporation: z.string().nullable(),
  entity_type: z.string().nullable(),
  entity_type_other_description: z.string().nullish(),
  is_participating_fatca: z.boolean().nullable(),
  is_exempt_from_tax_under_501a: z.boolean().nullable(),
  business_registration_number: z.string().nullable(),
  mailing_street_address: z.string().nullable(),
  mailing_unit: z.string().nullable(),
  mailing_city: z.string().nullable(),
  mailing_state: z.string().nullable(),
  mailing_postal_code: z.string().nullable(),
  mailing_country: z.string().nullable(),
  // Backend serializes these financial bands as strings on `/owners` (numbers
  // on `/details`); accept both so the owner isn't dropped. Consumers already
  // coerce via `Number(...)`.
  annual_income_min: z.union([z.number(), z.string()]).nullable(),
  annual_income_max: z.union([z.number(), z.string()]).nullable(),
  total_net_worth_min: z.union([z.number(), z.string()]).nullable(),
  total_net_worth_max: z.union([z.number(), z.string()]).nullable(),
  liquid_net_worth_min: z.union([z.number(), z.string()]).nullable(),
  liquid_net_worth_max: z.union([z.number(), z.string()]).nullable(),
  intended_use_of_account: z.string().nullable(),
  funding_source: z
    .union([z.string(), z.array(z.string())])
    .nullable()
    .nullish(),
  type_of_business: z.string().nullable(),
  type_of_business_other_free_text: z.string().nullable(),
  percentage_ownership: z.coerce.number().nullish(),
  primary: z.boolean().nullish(),
});

export const trustedContactSchema = z.object({
  account_id: z.string(),
  email_address: z.string(),
  phone_number: z.string(),
  street_address: z.union([z.string(), z.array(z.string())]).nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  postal_code: z.string().nullish(),
  country: z.string().nullish(),
  given_name: z.string(),
  family_name: z.string(),
});

export const documentRequestResponseSchema = z.object({
  id: z.string(),
  owner_id: z.string(),
  object_key: z.string(),
  object_metadata: z.record(z.string(), z.unknown()).nullish(),
  object_url: z.string(),
  // Widened with `z.string()`: a newly added document (sub)type must not drop
  // the whole document from the list. Consumers compare against known literals.
  doc_type: z.union([z.enum(DOCUMENT_TYPES), z.string()]),
  doc_sub_type: z.union([z.enum(DOCUMENT_SUB_TYPES), z.string()]),
  uploaded_by_admin_id: z.string().nullable(),
  requested_by_admin_id: z.string().nullable(),
  // Active/pending docs send null for unset timestamps. Without `.nullish()`,
  // `z.coerce.date()` would silently turn those into epoch 1970-01-01.
  requested_at: coerceDateNullish,
  uploaded_at: coerceDateNullish,
  created_at: coerceDate,
  updated_at: coerceDate,
  deleted_at: coerceDateNullish,
  message: z.string().nullable().optional(),
});

export const assetSchema = z.object({
  id: z.string(),
  class: z.string(),
  exchange: z.string(),
  symbol: z.string(),
  name: z.string(),
  status: z.string(),
  tradable: z.boolean(),
  marginable: z.boolean(),
  maintenance_margin_requirement: z.number(),
  shortable: z.boolean(),
  easy_to_borrow: z.boolean(),
  fractionable: z.boolean(),
});

export const watchlistSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  created_at: coerceDate,
  updated_at: coerceDate,
  name: z.string(),
  // The watchlist *list* endpoint omits `assets` (they come from the detail
  // endpoint), so tolerate a missing array; per-item parsing also prevents one
  // bad asset from dropping the whole watchlist.
  assets: looseArray(assetSchema, "watchlistSchema.assets").nullish(),
});

// `legs` omitted here and re-added below to break the self-reference; the TS
// `Order` type carries an optional array of `OrderLeg` (an `Order` without legs).
//
// Nullability here is deliberately generous: it is validated against real
// Broker API `/orders` payloads, not idealized fixtures. Notional orders send
// `qty: null` (and `notional` set), unfilled/open orders send
// `filled_avg_price: null`, and `position_intent` / `source` are absent on
// older or non-equity orders. If any of these were required, `looseArray`
// would silently drop the majority of real orders from the orders UI.
const orderBaseSchema = z.object({
  id: z.string(),
  client_order_id: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  submitted_at: z.string(),
  filled_at: z.string().nullable().optional(),
  expired_at: z.string().nullable().optional(),
  canceled_at: z.string().nullable().optional(),
  failed_at: z.string().nullable().optional(),
  replaced_at: z.string().nullable().optional(),
  replaced_by: z.string().nullable().optional(),
  replaces: z.string().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  asset_id: z.string(),
  symbol: z.string(),
  asset_class: z.string(),
  notional: z.string().nullable().optional(),
  qty: z.string().nullable().optional(),
  filled_qty: z.string(),
  ratio_qty: z.string().nullable().optional(),
  filled_avg_price: z.string().nullable().optional(),
  order_class: z.string(),
  order_type: z.string(),
  type: z.string(),
  side: z.string(),
  position_intent: z.string().nullable().optional(),
  time_in_force: z.string(),
  limit_price: z.string().nullable().optional(),
  stop_price: z.string().nullable().optional(),
  status: z.string(),
  extended_hours: z.boolean(),
  trail_percent: z.string().nullable().optional(),
  trail_price: z.string().nullable().optional(),
  hwm: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  destination: z.string().optional(),
  advanced_instructions: z
    .object({
      algorithm: z.string().optional(),
      destination: z.string().optional(),
      start_time: z.string().optional(),
      end_time: z.string().optional(),
      max_percentage: z.string().optional(),
    })
    .nullable()
    .optional(),
});

export const orderLegSchema = orderBaseSchema;

export const orderSchema = orderBaseSchema.extend({
  // Simple (non-bracket/OCO) orders return `legs: null`, not an omitted key —
  // `.nullish()` preserves null (looseArray alone would coerce null → []).
  // Per-item: one malformed leg must not drop the parent multi-leg order.
  legs: looseArray(orderLegSchema, "orderSchema.legs").nullish(),
});

export const paperAccountSchema = z.object({
  owner_id: z.string(),
  paper_account_id: z.string(),
  name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const marginSchema = z.object({
  id: z.string(),
  // Absent on most trade_account/margin payloads; consumers read via `?? 1`/`?? 0`.
  crypto_tier: z.number().optional(),
  account_number: z.string().nullish(),
  status: z.string(),
  crypto_status: z.string(),
  currency: z.string(),
  // Serialized as a decimal string (e.g. "0") like the sibling money fields;
  // coerce so a genuinely non-zero hold doesn't fail parse into the raw payload.
  held_ach_deposits: z.coerce.number().optional(),
  buying_power: z.string(),
  regt_buying_power: z.string(),
  effective_buying_power: z.string(),
  options_buying_power: z.string().optional(),
  non_marginable_buying_power: z.string(),
  // Omitted on real margin payloads (verified across live accounts).
  bod_dtbp: z.string().optional(),
  cash: z.string(),
  cash_withdrawable: z.string(),
  cash_transferable: z.string(),
  accrued_fees: z.string(),
  pending_transfer_out: z.string(),
  // Omitted on real margin payloads (verified across live accounts).
  pending_transfer_in: z.string().optional(),
  portfolio_value: z.string(),
  // These are real booleans on the backend: a genuinely blocked/suspended
  // account sends `true`. Using z.literal(false) made a blocked account fail
  // parse exactly when its risk state matters most.
  trading_blocked: z.boolean(),
  transfers_blocked: z.boolean(),
  account_blocked: z.boolean(),
  created_at: z.string(),
  trade_suspended_by_user: z.boolean(),
  multiplier: z.string(),
  shorting_enabled: z.boolean(),
  equity: z.string(),
  last_equity: z.string(),
  long_market_value: z.string(),
  short_market_value: z.string(),
  position_market_value: z.string(),
  initial_margin: z.string(),
  maintenance_margin: z.string(),
  last_maintenance_margin: z.string(),
  sma: z.string(),
  balance_asof: z.string(),
  clearing_broker: z.string(),
  // Dynamic fields present only on the `last=true` margin payload: `last_buying_power`
  // comes from the backend, while `last_effective_buying_power` / `last_excess_margin`
  // are computed client-side in `getMargin`. Optional so the base (current) payload
  // still validates and strip-unknown parsing preserves them when present.
  last_buying_power: z.string().optional(),
  last_effective_buying_power: z.number().optional(),
  last_excess_margin: z.number().optional(),
});

// Paper account details come from the margin endpoint but also carry account
// metadata used by options eligibility. Keep those fields endpoint-specific so
// the shared balance schema and live getMargin contract stay focused.
export const paperAccountMarginSchema = marginSchema.extend({
  options_approved_level: z.number().optional(),
  options_trading_level: z.number().optional(),
  admin_configurations: z
    .object({
      max_options_trading_level: z.number().optional(),
    })
    .optional(),
  user_configurations: userConfigurationsSchema.nullish(),
});

// Activities are polymorphic: trade activities (FILL/OPTRD…) carry
// symbol/side/price/qty, while non-trade activities (CSD/DIV/FEE…) carry
// date/description/per_share_amount/status. Only `id`/`activity_type` are
// guaranteed. Every other field uses `.default("")` so a missing key doesn't
// drop the activity via `looseArray` yet the type stays a plain string (no
// consumer changes / undefined guards required).
export const activitySchema = z.object({
  id: z.string(),
  activity_type: z.string(),
  activity_sub_type: z.string().optional(),
  date: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
  transaction_time: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
  net_amount: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
  description: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
  symbol: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
  side: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
  price: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
  qty: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
  per_share_amount: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
  status: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
});

export const wireBankStatusSchema = z.enum([
  "QUEUED",
  "SENT_TO_CLEARING",
  "APPROVED",
  "CANCELED",
  "REJECTED",
]);

export const bankSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  name: z.string(),
  status: wireBankStatusSchema,
  country: z.string(),
  state_province: z.string(),
  postal_code: z.string(),
  city: z.string(),
  street_address: z.string(),
  account_number: z.string(),
  bank_code: z.string(),
  bank_code_type: z.string(),
  created_at: coerceDate,
  updated_at: coerceDate,
});

export const nonIdentityAccountDocumentURLSchema = z.object({
  id: z.string(),
  url: z.string(),
});

export const nonIdentityAccountDocumentSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  sub_type: z.string(),
  date: z.string(),
});

// NOTE: The canonical option-contract schema lives in `data.schema.ts` (and is
// the one every consumer imports via `~/v2/api/rest/data`). A stricter,
// American-only duplicate previously lived here but was unused; it was removed
// to avoid drift. Add option-contract fields to `data.schema.ts`.

const optionsRequestStatusSchema = z.enum([
  "PENDING",
  "APPROVED",
  "LOWER_LEVEL_APPROVED",
  "REJECTED",
]);

const optionsLevelSchema = z.union(
  OPTIONS_LEVELS.map((level) => z.literal(level)) as [
    z.ZodLiteral<1>,
    z.ZodLiteral<2>,
    z.ZodLiteral<3>,
  ],
);

export const optionsApprovalStatusSchema = z.object({
  OptionsRequestStatus: optionsRequestStatusSchema.nullable(),
  ReapplyAvailableDate: z.string().nullable(),
  RequestedLevel: optionsLevelSchema.nullable(),
  ApprovedLevel: optionsLevelSchema.nullable(),
});

export const optionsApprovalResponseSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  requested_level: z.number().nullish(),
  status: optionsRequestStatusSchema,
});

export const fpslTierSchema = z.object({
  id: z.string(),
  market: z.string(),
  tier_name: z.string(),
  customer_split: z.number(),
});

export const fpslStatusSchema = z.object({
  tier_id: z.string(),
  status: z.enum(["ACTIVE", "INACTIVE"]),
});
